import { ButtonIcon, Col, Grid, Icon, Row, Typography } from "@visa/vds";
import MCSControlInput from "components/MCSControlInput";
import MCSControlTextArea from "components/MCSControlTextArea";
import React, { useContext, useEffect, useState } from "react";
import { GlobalAlertContext } from "context/GlobalAlertContext";
import { trackPromise } from "react-promise-tracker";
import { ceritificationPackageService } from "services/certification.certificationPackage";
import CertificationPackageDialog from "./CertificationPackageDialog";
import MCSLoading from "components/MCSLoading";
import { MS_DYNAMICS_CASES_REGEX } from "_constants/CertificationTestingConstants";
import { PlusOutlined } from '@ant-design/icons';

const CertificationPackageCompanyNotes = ({
    control,
    handleChange,
    register,
    IsInternalNotesViewable,
    IsInternalNotesEditable,
    IsNotesToClientViewable,
    IsClientNotesEditable,
    IsScreenshotsUploadable,
    IsScreenshotsViewable,
    packageId,
}) => {
    const [uploadedScreenshots, setUploadedScreenshots] = useState([]);
    const [pendingScreenshots, setPendingScreenshots] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [showErrorModal, setShowErrorDialog] = useState(false);
    const [showdeleteDialog, setShowDeleteDialog] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [isNotUploadable, setIsNotUploadable] = useState(false);
    const alertContext = useContext(GlobalAlertContext);
    const [currentDocument, setCurrentDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const loadScreenshots = async () => {
        setIsLoading(true);
        trackPromise(
            ceritificationPackageService.GetCertificationPackageScreenshots(packageId)
                .then((response) => {
                    if (response.status === 200) {
                        response.data.text().then((text) => {
                            const jsonData = JSON.parse(text);
                            if (jsonData.result.length > 0) {
                                setUploadedScreenshots(jsonData.result);
                            }
                        }).catch((e) => {
                            alertContext.setErrorMessage("Error Parsing Certification Package Screenshots Data");
                        });
                    } else {
                        alertContext.setErrorMessage("Error Loading Certification Package Screenshots");
                    }
                })
                .catch(() => alertContext.setErrorMessage("Error Loading Certification Package Screenshots"))
                .finally(() => setIsLoading(false))
        );
    }

    useEffect(() => { handleUploadableLogic(); }, [isNotUploadable, pendingScreenshots, uploadedScreenshots]);
    useEffect(() => { loadScreenshots(); }, [pendingScreenshots]);
    useEffect(() => { }, [isLoading]);

    const handleScreenshotChanges = async (event) => {
        const input = event.target;
        const selectedScreenshots = Array.from(event.target.files);
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (selectedScreenshots.length === 0) {
            return;
        }
        for (const ss of selectedScreenshots) {
            const errorsWithScreenshots = []
            if (ss.size > 2 * 1024 * 1024) {
                errorsWithScreenshots.push("Invalid file size, each file must not exceed 2MB. ");
            }
            if (ss.name.length >= 30) {
                errorsWithScreenshots.push("Invalid file name, each file must be less than 30 characters. ");
            }
            if (!validTypes.includes(ss.type)) {
                errorsWithScreenshots.push('Invalid file type, each file must be of type jpg, jpeg, or png.');
            }
            if (errorsWithScreenshots.length > 0) {
                setErrorMessage(errorsWithScreenshots.join('\n'));
                setShowErrorDialog(true);
                input.value = '';
                return;
            }
            setPendingScreenshots((prevFiles) => [...prevFiles, ss]);
        }
        setErrorMessage("");
        input.value = '';
    };

    const handleUploadableLogic = () => {
        if ((pendingScreenshots.length + uploadedScreenshots.length) > 10) {
            setIsNotUploadable(true);
        } else {
            setIsNotUploadable(false);
        }
    };

    const handleUpload = async () => {
        if (pendingScreenshots.length === 0) {
            return;
        }
        const screenshots = await Promise.all(pendingScreenshots.map(async (file) => {
            const base64 = await convertFileToBase64(file);
            const date = new Date().toISOString().split('T')[0];
            const fileNameWithDate = `${file.name.split('.')[0]}_${date}.${file.name.split('.').pop()}`;
            return {
                fileName: fileNameWithDate,
                hasFile: true,
                fileData: base64
            };
        }));
        try {
            setIsLoading(true);
            const response = await ceritificationPackageService.UploadCertificationPackageScreenshots(packageId, screenshots);
            if (response.status === 200) {
                setPendingScreenshots([]);
            } else {
                alertContext.setErrorMessage("Error uploading screenshots");
            }
        } catch (error) {
            alertContext.setErrorMessage("Error Uploading Certification Package Screenshots");
        } finally {
            setIsLoading(false);
        }
    };

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result;
                if (typeof result === 'string') {
                    resolve(result.split(',')[1]);
                } else {
                    reject(new Error('FileReader result is not a string'));
                }
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handlePreview = (screenshot) => {
        const reader = new FileReader();
        reader.onload = () => {
            setPreviewImage(reader.result);
            setShowModal(true);
        }
        reader.readAsDataURL(screenshot);
    };

    const selectFiles = () => {
        document.getElementById('fileInput').click();
    };

    const handleDelete = (index, type, s3Key) => {
        if (type === 'pending') {
            setPendingScreenshots((prevFiles) => prevFiles.filter((_, i) => i !== index));
        }
        else {
            try {
                deleteFromDBS3(s3Key);
                setUploadedScreenshots((prevFiles) => prevFiles.filter((_, i) => i !== index));
            } catch (error) {
                alertContext.setErrorMessage("Error Deleting Certification Package Screenshots");
            }
        }
    };

    const closeModal = () => {
        setShowErrorDialog(false);
    };

    const closePreviewModal = () => {
        setShowModal(false);
        setPreviewImage(null);
    };

    const deleteFromDBS3 = async (s3Key) => {
        try {
            setIsLoading(true);
            const response = await ceritificationPackageService.DeleteCertificationPackageScreenshot(packageId, s3Key);
            if (response.status === 200) {
                console.log("Successfully deleted");
            }
            else {
                alertContext.setErrorMessage("Delete failed.");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (s3Key) => {
        try {
            if (s3Key == null) {
                return;
            }
            setIsLoading(true);
            const retrievedFile = await ceritificationPackageService.GetCertificationPackageScreenshot(packageId, s3Key);
            if (retrievedFile.status === 200) {
                const reader = new FileReader();
                reader.onload = () => {
                    setPreviewImage(reader.result);
                    setShowModal(true);
                };
                reader.readAsDataURL(retrievedFile.data);
            } else {
                alertContext.setErrorMessage("Error retrieving screenshot");
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (<MCSLoading />)
    }
    else {
        return (
            <>
                <CertificationPackageDialog dialogType="warning" open={showdeleteDialog} setOpen={setShowDeleteDialog} title="Warning"
                    leftButton={{ color: "secondary", text: "Delete", handleClick: () => handleDelete() }}
                    rightButton={{ color: "primary", text: "Close" }} >
                    This action will delete &nbsp; <b>{currentDocument && currentDocument.document && currentDocument.document.fileName}</b>
                </CertificationPackageDialog>
                <CertificationPackageDialog dialogType="error" open={showErrorModal} setOpen={setShowErrorDialog} title="Error"
                    leftButton={false}
                    rightButton={{ color: "primary", text: "Close", handleClick: closeModal }}>
                    <b>{errorMessage}</b>
                </CertificationPackageDialog>
                <CertificationPackageDialog open={showModal} setOpen={setShowModal} title="Preview"
                    leftButton={false}
                    rightButton={{ color: "primary", text: "Close", handleClick: () => closePreviewModal() }}>
                    <img src={previewImage} alt="Preview" className="certification-package-screenshots-preview" />
                </CertificationPackageDialog>
                <Row>
                    <Col>
                        <Typography className="certification-package-screenshots-font-size">Client Notes</Typography>
                        <div className="certification-package-screenshots-padding-left">
                            <MCSControlTextArea
                                readOnly={!IsClientNotesEditable}
                                register={register} control={control}
                                handleChange={handleChange}
                                name={"clientNotes"}
                                rules={{ maxLength: 500 }} />
                        </div>
                    </Col>
                    {IsNotesToClientViewable &&
                        <>
                            <Col>
                                <Typography className="certification-package-screenshots-font-size">Visa Notes</Typography>
                                <div className="certification-package-screenshots-padding-left">
                                    <MCSControlTextArea
                                        readOnly={!IsInternalNotesEditable}
                                        register={register}
                                        control={control}
                                        handleChange={handleChange}
                                        name={"notesToClient"}
                                        rules={IsInternalNotesEditable ? { maxLength: 500, required: true, } : {}} />
                                </div>
                            </Col>
                        </>
                    }
                </Row>
                <Row>
                    {IsInternalNotesViewable &&
                        <Row>
                            <Col>
                                <Typography>Internal Notes (Not visible to client)</Typography>
                                <div className="certification-package-screenshots-padding-left">
                                    <MCSControlInput
                                        readOnly={!IsInternalNotesEditable}
                                        register={register}
                                        control={control}
                                        handleChange={handleChange}
                                        name={"internalNotes"}
                                        rules={{ maxLength: 500 }}
                                    />
                                </div>
                            </Col>
                            <Col>
                                <Typography>MS Dynamics Cases (Not visible to client)</Typography>
                                <div className="certification-package-screenshots-padding-left">
                                    <MCSControlInput
                                        readOnly={!IsInternalNotesEditable} register={register} control={control} handleChange={handleChange}
                                        name={"msDynamicsCases"}
                                        rules={
                                            IsInternalNotesEditable ?
                                                {
                                                    required: true,
                                                    validate: { validateMsDynamicsCases: (v) => MS_DYNAMICS_CASES_REGEX.test(v) },
                                                } : {}
                                        }
                                    />
                                </div>
                            </Col>
                        </Row>
                    }
                    {(IsScreenshotsUploadable) &&
                        <Row>
                            <Col />
                            <Col>
                                <Row className="certification-package-screenshots-attach">
                                    <Typography className="certification-package-screenshots-custom">Visa Attach Images</Typography>
                                    <ButtonIcon
                                        ariaLabel="close"
                                        iconType="light"
                                        disabled={isNotUploadable}
                                        onClick={() => { selectFiles() }}
                                        className="certification-package-screenshots-button">

                                        <PlusOutlined className="certification-package-screenshots-greyed-icon" />
                                        <input
                                            id="fileInput"
                                            type="file"
                                            multiple
                                            onChange={(e) => handleScreenshotChanges(e)}
                                            className="certification-package-screenshots-input-icon"
                                            disabled={isNotUploadable}
                                        />
                                        <Typography className="certification-package-screenshots-typography-custom">Choose Images</Typography>
                                    </ButtonIcon>
                                </Row>
                                <Typography className="certification-package-screenshots-padding-left"> {" Attach Images is an admin-only feature. 1) Only JPG and PNG File types are supported; 2) Max image size of 2MB for each image; 3) Max of 10 images total allowed"}</Typography>
                                <Row>
                                    {(pendingScreenshots.length > 0) && IsScreenshotsUploadable &&
                                        <>
                                            <Col>
                                                <Typography>Pending Images</Typography>
                                                <div className="certification-package-box-outline">
                                                    <Grid>
                                                        {pendingScreenshots.map((file, index) => (
                                                            <Row key={index} className="certification-package-screenshots-pending">
                                                                <span onClick={() => handlePreview(file)}>{`(${index + 1}) ${file.name}`}</span>
                                                                <Icon name="close"
                                                                    resolution="tiny"
                                                                    aria-label="close"
                                                                    onClick={() => { handleDelete(index, 'pending'); }} />
                                                            </Row>
                                                        ))}
                                                    </Grid>
                                                </div>
                                            </Col>
                                            <Col className="certification-package-screenshots-confirmation-button">
                                                <ButtonIcon ariaLabel="close" iconType="light-outline" disabled={isNotUploadable}>
                                                    <Icon name="media-fast-forward"
                                                        resolution="tiny"
                                                        aria-label="media fast forward" onClick={handleUpload} disabled={isNotUploadable} />
                                                </ButtonIcon>
                                            </Col>
                                        </>
                                    }
                                    <Col>
                                        {(uploadedScreenshots.length > 0) && (IsScreenshotsViewable) &&
                                            <>
                                                <Typography >Visa Attached Images</Typography>
                                                <div className="certification-package-box-outline">
                                                    <Grid >
                                                        {uploadedScreenshots.map((file, index) => (
                                                            <Row key={index} className="certification-package-screenshots-uploaded">
                                                                <span onClick={() => handleDownload(file.s3DocumentId)}>{`(${index + 1}) ${file.s3Document.fileName}`}</span>
                                                                <Icon name="close"
                                                                    resolution="tiny"
                                                                    aria-label="close" onClick={() => { handleDelete(index, 'uploaded', file.s3DocumentId) }} />
                                                            </Row>
                                                        ))}
                                                    </Grid>
                                                </div>
                                            </>
                                        }
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    }
                    {(!IsScreenshotsUploadable) &&
                        <Row>
                            <Col />
                            <Col>
                                {(uploadedScreenshots.length > 0) && (uploadedScreenshots.length > 0) && (IsScreenshotsViewable) &&
                                    <>
                                        <Typography>Visa Attached Images</Typography>
                                        <div className={IsScreenshotsUploadable ? '' : 'certification-package-screenshots-padding-left'}>
                                            <div className="certification-package-box-outline">
                                                <Grid>
                                                    {uploadedScreenshots.map((file, index) => (
                                                        <Row key={index} className="certification-package-screenshots-uploaded">
                                                            <span onClick={() => handleDownload(file.s3DocumentId)}>{`(${index + 1}) ${file.s3Document.fileName}`}</span>
                                                        </Row>
                                                    ))}
                                                </Grid>
                                            </div>
                                        </div>
                                    </>
                                }
                            </Col>
                        </Row>

                    }
                </Row>
            </>
        );
    };
}

export default CertificationPackageCompanyNotes;